/**
 * A character: what one is, and what the drawing needs to know to draw it.
 *
 * ## Ported, not reinvented
 *
 * The shapes, the palettes and the expression table all come from the
 * generator in `dev/`. The drawing itself is machine-converted from that
 * template - four hundred and thirty-five shapes across a hundred and
 * seventy-nine conditionals, which is far too many to retype without quietly
 * getting one path wrong. This file is the other half: the small amount of
 * arithmetic the template reads.
 *
 * ## A config, not an image
 *
 * Nine words and eight colours. That is bytes rather than an upload, so there is
 * no presigned URL, no processing job, and nothing to orphan in a bucket - and
 * because it is structured, a character can be re-opened and edited rather than
 * replaced. It also cannot be a slur or a photograph of somebody, which an
 * uploaded avatar can.
 */

export interface Character {
  hair: string;
  face: string;
  expr: string;
  outfit: string;
  legs: string;
  acc: string;
  item: string;
  /**
   * A full mascot head, worn over everything.
   *
   * Its own slot rather than another `acc`, because it is not an accessory: it
   * replaces the face, the facial hair and the front hair rather than sitting
   * on top of them, and a list where one entry silently disables three other
   * pickers is a list that reads as broken.
   */
  mask: string;
  /**
   * A print across the chest.
   *
   * Only drawn on a tee, a band tee or a tie-dye - see `showGraphic` - because
   * those are the three tops with a flat front to put one on. It is kept when
   * the outfit changes rather than cleared, so somebody trying a jacket on and
   * going back to the tee finds their print still there.
   */
  graphic: string;
  wrist: boolean;
  chain: boolean;
  ear: boolean;
  hairColor: string;
  eyeColor: string;
  outfitColor: string;
  skinColor: string;
  shoeColor: string;
  maskColor: string;
  /**
   * Trousers, joggers and skirts.
   *
   * Their own colour since v1.2. They used to be `shade(outfitColor, -46)`,
   * which meant a red top could only ever have dark red legs - see `normalise`
   * for how a character saved before this keeps exactly the legs it had.
   */
  legColor: string;
  /** The ink the chest print is drawn in. Chosen, not derived from the top. */
  graphicColor: string;
}

/** What a character starts as, before anybody chooses anything. */
export const DEFAULT: Character = {
  hair: 'swept',
  face: 'none',
  expr: 'neutral',
  outfit: 'jacket',
  legs: 'pants',
  acc: 'band',
  item: 'none',
  mask: 'none',
  /*
   * No print, where the generator ships a lightning bolt.
   *
   * `DEFAULT` is also what `normalise` falls back to for a field a stored
   * character does not have, and every character saved before v1.2 is missing
   * this one. A bolt here would print itself across the chest of everybody who
   * already owns a tee, which is a change to somebody's face that they did not
   * ask for.
   */
  graphic: 'none',
  wrist: false,
  chain: false,
  ear: false,
  hairColor: '#3a3a45',
  eyeColor: '#3f6bd8',
  outfitColor: '#8f95a3',
  skinColor: '#f6d3ae',
  shoeColor: '#4a2f22',
  maskColor: '#8d8598',
  legColor: '#3f4a63',
  graphicColor: '#f4efe6',
};

/** Every choice, with the words a person reads rather than the keys stored. */
export const OPTIONS = {
  expr: [
    ['neutral', 'Neutral'], ['happy', 'Happy'], ['laughing', 'Laughing'], ['sad', 'Sad'],
    ['angry', 'Angry'], ['surprised', 'Surprised'], ['wink', 'Wink'], ['smug', 'Smug'],
    ['love', 'Hearts'], ['dizzy', 'Dizzy'],
  ],
  // In the generator's order, which is roughly short to long. The stepper walks
  // this list, so the order is what somebody experiences.
  hair: [
    ['swept', 'Side-swept'], ['spiky', 'Spiky'], ['quiff', 'Quiff'], ['slick', 'Slick back'],
    ['under', 'Undercut'], ['buzz', 'Buzz fade'], ['flat', 'Flat top'], ['messy', 'Bedhead'],
    ['curtain', 'Curtains'], ['mullet', 'Mullet'], ['mohawk', 'Mohawk'],
    ['liberty', 'Liberty spikes'], ['rows', 'Cornrows'], ['dreads', 'Locs'], ['curls', 'Curls'],
    ['afro', 'Afro'], ['long', 'Long'], ['bun', 'Top bun'], ['sbuns', 'Space buns'],
    ['braids', 'Twin braids'], ['tail', 'Ponytail'], ['half', 'Half-shave'],
    ['bald', 'Receding'], ['shaved', 'Shaved'],
  ],
  face: [
    ['none', 'Clean'], ['stubble', 'Stubble'], ['moustache', 'Moustache'],
    ['handlebar', 'Handlebar'], ['goatee', 'Goatee'], ['chinstrap', 'Chin strap'],
    ['chops', 'Mutton chops'], ['beard', 'Full beard'],
  ],
  outfit: [
    ['jacket', 'Jacket + scarf'], ['hoodie', 'Hoodie'], ['tee', 'Tee'], ['band', 'Band tee'],
    ['tiedye', 'Tie-dye'], ['tank', 'Tank'], ['leather', 'Leather jacket'],
    ['track', 'Track jacket'], ['blazer', 'Blazer + tie'], ['jersey', 'Jersey'],
    ['flannel', 'Flannel'], ['varsity', 'Varsity'],
  ],
  legs: [['pants', 'Pants'], ['jogger', 'Joggers'], ['shorts', 'Shorts'], ['skirt', 'Skirt']],
  acc: [
    ['none', 'None'], ['band', 'Headband'], ['bandana', 'Bandana'], ['cap', 'Cap'],
    ['bucket', 'Bucket hat'], ['beanie', 'Beanie'], ['glasses', 'Glasses'], ['shades', 'Shades'],
    ['phones', 'Headphones'],
  ],
  item: [
    ['none', 'Empty'], ['glow', 'Glowstick'], ['phone', 'Phone'], ['cup', 'Drink'],
    ['vinyl', 'Vinyl'], ['mic', 'Mic'],
  ],
  mask: [
    ['none', 'None'], ['mouse', 'Mouse'], ['cat', 'Cat'], ['bear', 'Bear'], ['frog', 'Frog'],
    ['robot', 'Robot'],
  ],
  graphic: [
    ['none', 'None'], ['vibed', 'Vibed logo'], ['bolt', 'Bolt'], ['heart', 'Heart'],
    ['star', 'Star'], ['note', 'Music note'], ['skull', 'Skull'], ['smiley', 'Smiley'],
    ['peace', 'Peace'], ['flame', 'Flame'], ['diamond', 'Diamond'], ['moon', 'Moon'],
    ['wave', 'Wave'], ['eye', 'Eye'], ['yin', 'Yin-yang'],
  ],
} as const;

/**
 * The tops a chest print is drawn on.
 *
 * Three, and they are the three with a flat front. Everything else is a jacket,
 * a shirt or a tank, where a print would sit over a zip, a collar or nothing at
 * all. The maker reads this to decide whether to offer the pickers, and
 * `values` reads it to decide whether to draw - one list, so the control cannot
 * be offered for something that would not render.
 */
export const GRAPHIC_TOPS = ['tee', 'band', 'tiedye'] as const;

/** The colours on offer, per part. */
export const PALETTE = {
  hairColor: [
    '#3a3a45', '#1c1a20', '#6b4a2f', '#a8703c', '#c9a227', '#b8452f', '#e754a6', '#7a5cc4',
    '#3f7f8f', '#3fbf6f', '#e8e4dc',
  ],
  eyeColor: ['#3f6bd8', '#2f9fb8', '#4a8f5c', '#8fae4a', '#a8763c', '#7a4a2f', '#5a4a8f', '#b8452f', '#2b2b33'],
  outfitColor: ['#8f95a3', '#3f4a63', '#a34b5e', '#4a6b53', '#2b2b33', '#c9743f', '#6b5aa8', '#d8cfc0'],
  skinColor: ['#f8dcc0', '#f6d3ae', '#eab88a', '#d09a63', '#c98a5e', '#8d5a3b', '#5c3a28'],
  shoeColor: ['#4a2f22', '#241f26', '#e8e4dc', '#b8452f', '#3f4a63'],
  maskColor: ['#8d8598', '#c9a684', '#e754a6', '#4a6b53', '#7a5cc4', '#e8e4dc'],
  legColor: ['#3f4a63', '#2b2b33', '#8f95a3', '#6b4a2f', '#a34b5e', '#4a6b53', '#d8cfc0', '#7a5cc4'],
  graphicColor: ['#f4efe6', '#241f26', '#b8362f', '#e754a6', '#c9a227', '#3f6bd8', '#3fbf6f', '#7B84F2'],
} satisfies Record<string, string[]>;

/** Brow, eye and mouth for each expression. */
const EXPR: Record<string, { brow: string; eye: string; mouth: string }> = {
  neutral: { brow: 'flat', eye: 'open', mouth: 'neutral' },
  happy: { brow: 'raised', eye: 'happy', mouth: 'smile' },
  laughing: { brow: 'raised', eye: 'happy', mouth: 'laugh' },
  sad: { brow: 'sad', eye: 'open', mouth: 'frown' },
  angry: { brow: 'angry', eye: 'open', mouth: 'grit' },
  surprised: { brow: 'raised', eye: 'open', mouth: 'oh' },
  wink: { brow: 'raised', eye: 'wink', mouth: 'smirk' },
  smug: { brow: 'flat', eye: 'lid', mouth: 'smirk' },
  love: { brow: 'raised', eye: 'heart', mouth: 'smile' },
  dizzy: { brow: 'flat', eye: 'x', mouth: 'oh' },
};

/** Lighten or darken a hex colour by a flat amount per channel. */
function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);

  return (
    '#' +
    [n >> 16, (n >> 8) & 255, n & 255]
      .map((channel) =>
        Math.max(0, Math.min(255, Math.round(channel + amount)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/**
 * Anything unrecognised falls back to the default rather than drawing nothing.
 *
 * A stored character is a row of words that will outlive the option list it was
 * written from - somebody's hairstyle can be retired, and their face should not
 * disappear when it is. Same argument as the profile's widget layout.
 */
export function normalise(stored: unknown): Character {
  const raw = (stored ?? {}) as Partial<Character>;
  const pick = (key: keyof typeof OPTIONS, value: unknown): string => {
    const known = (OPTIONS[key] as readonly (readonly [string, string])[]).some(
      ([option]) => option === value,
    );

    return known ? (value as string) : (DEFAULT[key] as string);
  };

  const colour = (key: string, value: unknown): string =>
    typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
      ? value
      : (DEFAULT[key as keyof Character] as string);

  return {
    hair: pick('hair', raw.hair),
    face: pick('face', raw.face),
    expr: pick('expr', raw.expr),
    outfit: pick('outfit', raw.outfit),
    legs: pick('legs', raw.legs),
    acc: pick('acc', raw.acc),
    item: pick('item', raw.item),
    mask: pick('mask', raw.mask),
    graphic: pick('graphic', raw.graphic),
    wrist: raw.wrist === true,
    chain: raw.chain === true,
    ear: raw.ear === true,
    hairColor: colour('hairColor', raw.hairColor),
    eyeColor: colour('eyeColor', raw.eyeColor),
    outfitColor: colour('outfitColor', raw.outfitColor),
    skinColor: colour('skinColor', raw.skinColor),
    shoeColor: colour('shoeColor', raw.shoeColor),
    maskColor: colour('maskColor', raw.maskColor),
    /*
     * The v1.2 migration, done at read time rather than as a pass over the
     * table.
     *
     * Before v1.2 the legs were `shade(outfitColor, -46)` and there was no
     * `legColor` to store. Falling back to the palette default here would give
     * every character saved before the split a pair of navy trousers they never
     * chose - so the fallback is the old derivation, which means they keep
     * exactly the legs they have and only gain the ability to change them.
     *
     * Nothing has to be written back. A character is normalised on every read,
     * so the old shape and the new one both arrive as the new one, and the row
     * is rewritten the next time somebody saves.
     */
    legColor:
      typeof raw.legColor === 'string' && /^#[0-9a-f]{6}$/i.test(raw.legColor)
        ? raw.legColor
        : shade(colour('outfitColor', raw.outfitColor), -46),
    graphicColor: colour('graphicColor', raw.graphicColor),
  };
}

/**
 * A character from a string, the same one every time.
 *
 * Seeded from a username so somebody who has never opened the maker still has a
 * face, and the same face on every screen and every device. A random default
 * would change on each render, which is worse than a letter in a circle.
 */
export function fromSeed(seed: string): Character {
  let hash = 2166136261;

  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  // A fresh value per part, so two people who differ in one character do not
  // end up differing in one feature.
  const next = (): number => {
    hash = Math.imul(hash ^ (hash >>> 15), 2246822507);
    hash = Math.imul(hash ^ (hash >>> 13), 3266489909);

    return ((hash ^= hash >>> 16) >>> 0) / 4294967296;
  };

  const one = <T,>(list: readonly T[]): T => list[Math.floor(next() * list.length)] as T;

  /*
   * Everything rolled, in one literal, because the order is the identity.
   *
   * Each `next()` advances the same stream, so the sequence of draws below is
   * what makes a username produce the character it does. Inserting a new roll
   * anywhere but the end would shift every value after it and hand everybody a
   * different face - which is why `legColor` is derived underneath rather than
   * drawn here.
   */
  const rolled: Omit<Character, 'legColor'> = {
    hair: one(OPTIONS.hair)[0],
    face: one(OPTIONS.face)[0],
    // Seeded characters are pleased to be there. Assigning somebody a face that
    // looks angry or dizzy without them choosing it is a worse first
    // impression than any hairstyle.
    expr: one([['neutral'], ['happy'], ['smug'], ['wink']] as const)[0],
    outfit: one(OPTIONS.outfit)[0],
    legs: one(OPTIONS.legs)[0],
    acc: one(OPTIONS.acc)[0],
    item: 'none',
    /*
     * Never a mask, for the reason the expression above is never `dizzy`.
     *
     * A seeded character is what somebody is shown as before they have chosen
     * anything, and handing a stranger a robot head is a much stronger claim
     * about them than any hairstyle. Masks are worn on purpose or not at all.
     */
    mask: 'none',
    wrist: next() > 0.7,
    chain: next() > 0.8,
    ear: next() > 0.75,
    hairColor: one(PALETTE.hairColor),
    eyeColor: one(PALETTE.eyeColor),
    outfitColor: one(PALETTE.outfitColor),
    skinColor: one(PALETTE.skinColor),
    shoeColor: one(PALETTE.shoeColor),
    // Follows the default rather than being rolled: nothing is wearing a mask
    // until somebody picks one, so the colour under it never shows.
    maskColor: DEFAULT.maskColor,
    /*
     * Never a print, for the reason the mask above is never a mask.
     *
     * A seeded character is what somebody is shown as before they have chosen
     * anything, and a skull across a stranger's chest is a claim about them
     * that a hairstyle is not.
     */
    graphic: 'none',
    graphicColor: DEFAULT.graphicColor,
  };

  /*
   * The legs follow the top, exactly as they did before they could be chosen.
   *
   * Everybody who has never opened the maker is wearing a seeded character
   * right now, and every one of those has legs at `shade(outfitColor, -46)`.
   * Rolling a colour from the new palette instead would repaint the trousers of
   * every such account at once - a change to a lot of people's faces in
   * exchange for nothing anybody asked for.
   */
  return { ...rolled, legColor: shade(rolled.outfitColor, -46) };
}

/** Every flag and colour the drawing reads. Ported from the generator. */
export function values(character: Character) {
  const c = normalise(character);
  // `??` is not enough for the checker here: an index into a Record is
  // possibly-undefined either side of it, and `neutral` is the one key this
  // file guarantees exists.
  const expression = EXPR[c.expr] ?? { brow: 'flat', eye: 'open', mouth: 'neutral' };
  const inner = shade(c.outfitColor, 58);

  return {
    skin: c.skinColor,
    hair: c.hairColor,
    eye: c.eyeColor,
    outfit: c.outfitColor,
    inner,
    accent: '#b8362f',
    shoe: c.shoeColor,
    hairLight: shade(c.hairColor, 48),
    hairDark: shade(c.hairColor, -26),
    pocket: shade(c.outfitColor, -28),
    /*
     * All three from `legColor` since v1.2, where they were derivations of the
     * top. A skirt in particular used to be `shade(outfitColor, -18)` and is
     * now the leg colour like everything else below the waist - which for a
     * character saved before the split is a slightly darker skirt, because the
     * migration in `normalise` reproduces the trouser derivation rather than
     * the skirt one. One colour for the legs is worth that.
     */
    legFill: c.legColor,
    skirtFill: c.legColor,
    legInner: shade(c.legColor, 52),

    sleeveLong: ['jacket', 'hoodie', 'blazer', 'flannel', 'varsity', 'leather', 'track']
      .includes(c.outfit),
    sleeveShort: ['tee', 'jersey', 'band', 'tiedye'].includes(c.outfit),
    sleeveFill: c.outfit === 'varsity' ? inner : c.outfitColor,

    hSwept: c.hair === 'swept', hSpiky: c.hair === 'spiky', hQuiff: c.hair === 'quiff',
    hUnder: c.hair === 'under', hBuzz: c.hair === 'buzz', hMohawk: c.hair === 'mohawk',
    hCurls: c.hair === 'curls', hBun: c.hair === 'bun', hTail: c.hair === 'tail',
    hSlick: c.hair === 'slick', hCurtain: c.hair === 'curtain', hDreads: c.hair === 'dreads',
    hRows: c.hair === 'rows', hLong: c.hair === 'long', hMessy: c.hair === 'messy',
    hFlat: c.hair === 'flat', hMullet: c.hair === 'mullet', hBald: c.hair === 'bald',
    hLiberty: c.hair === 'liberty', hAfro: c.hair === 'afro', hSbuns: c.hair === 'sbuns',
    hBraids: c.hair === 'braids', hHalf: c.hair === 'half',

    fStubble: c.face === 'stubble', fMoustache: c.face === 'moustache',
    fGoatee: c.face === 'goatee', fBeard: c.face === 'beard',
    fChinstrap: c.face === 'chinstrap', fChops: c.face === 'chops',
    fHandlebar: c.face === 'handlebar',

    bFlat: expression.brow === 'flat', bRaised: expression.brow === 'raised',
    bAngry: expression.brow === 'angry', bSad: expression.brow === 'sad',
    eOpen: expression.eye === 'open', eLid: expression.eye === 'lid',
    eHappy: expression.eye === 'happy', eWink: expression.eye === 'wink',
    eHeart: expression.eye === 'heart', eX: expression.eye === 'x',
    mNeutral: expression.mouth === 'neutral', mSmile: expression.mouth === 'smile',
    mLaugh: expression.mouth === 'laugh', mFrown: expression.mouth === 'frown',
    mGrit: expression.mouth === 'grit', mOh: expression.mouth === 'oh',
    mSmirk: expression.mouth === 'smirk',

    oJacket: c.outfit === 'jacket', oHoodie: c.outfit === 'hoodie', oTee: c.outfit === 'tee',
    oTank: c.outfit === 'tank', oBlazer: c.outfit === 'blazer', oJersey: c.outfit === 'jersey',
    oFlannel: c.outfit === 'flannel', oVarsity: c.outfit === 'varsity',
    oBand: c.outfit === 'band', oLeather: c.outfit === 'leather',
    oTiedye: c.outfit === 'tiedye', oTrack: c.outfit === 'track',

    lShorts: c.legs === 'shorts', lSkirt: c.legs === 'skirt', lJogger: c.legs === 'jogger',

    aBand: c.acc === 'band', aCap: c.acc === 'cap', aBeanie: c.acc === 'beanie',
    aGlasses: c.acc === 'glasses', aShades: c.acc === 'shades', aPhones: c.acc === 'phones',
    aBucket: c.acc === 'bucket', aBandana: c.acc === 'bandana',

    iGlow: c.item === 'glow', iPhone: c.item === 'phone', iCup: c.item === 'cup',
    iVinyl: c.item === 'vinyl', iMic: c.item === 'mic',
    jWrist: c.wrist, jChain: c.chain, jEar: c.ear,

    /*
     * The chest print, front view only, on the three flat-fronted tops.
     *
     * Both halves of the condition matter and neither is redundant: a print is
     * only drawn on a top that has room for one, and `none` is a real choice
     * rather than the absence of one. The maker offers the picker on exactly
     * the same list - see `GRAPHIC_TOPS` - so a control can never be offered
     * for something that would not render.
     *
     * `gfx` is the ink, and it is chosen rather than derived. The drop shipped
     * it as an automatic contrast against the top; that quietly forbids a black
     * print on a dark shirt, which is most of what a band tee actually is.
     * A few of the prints still punch their counter-shapes through in
     * `outfit` - the skull's eyes, the diamond's facets - so those read as
     * holes in the ink rather than as a second colour.
     */
    showGraphic: (GRAPHIC_TOPS as readonly string[]).includes(c.outfit) && c.graphic !== 'none',
    gVibed: c.graphic === 'vibed', gBolt: c.graphic === 'bolt', gHeart: c.graphic === 'heart',
    gStar: c.graphic === 'star', gNote: c.graphic === 'note', gSkull: c.graphic === 'skull',
    gSmiley: c.graphic === 'smiley', gPeace: c.graphic === 'peace', gFlame: c.graphic === 'flame',
    gDiamond: c.graphic === 'diamond', gMoon: c.graphic === 'moon', gWave: c.graphic === 'wave',
    gEye: c.graphic === 'eye', gYin: c.graphic === 'yin',
    gfx: c.graphicColor,

    /*
     * The mask draws over the head, the face and the front hair - after
     * `hair-front` and before the accessories, so a cap or headphones still go
     * on top of it.
     *
     * Inside `.head-sync` with everything else on the head. The drop's notes
     * describe it as sitting outside, so that a head-bob moves the body under a
     * stationary mask; the generator it shipped with does not do that, and the
     * generator is right. The mask is the head rect exactly, so leaving it
     * behind a five-pixel bob uncovers skin along the top - which reads as a
     * bug rather than as a loose mascot head.
     */
    mMouse: c.mask === 'mouse', mCat: c.mask === 'cat', mBear: c.mask === 'bear',
    mFrog: c.mask === 'frog', mRobot: c.mask === 'robot',
    maskFill: c.maskColor,

    // The generator animates; this does not, yet. The class is what the
    // drawing expects, and an idle character is the right thing in a crowd
    // that may have thirty of them on screen at once.
    animClass: 'a-idle',
  };
}

export type CharacterValues = ReturnType<typeof values>;
